export type ReferenceIdentifierKind = "doi" | "pmid" | "isbn" | "arxiv";

export interface ReferenceIdentifier {
  kind: ReferenceIdentifierKind;
  value: string;
  input: string;
  provenance:
    | "DOI registry lookup"
    | "PubMed"
    | "Open Library"
    | "DataCite lookup (arXiv DOI)";
}

const DOI_REGEX = /\b10\.\d{4,9}\/[^\s"'<>{}[\]]+/i;
const DOI_URL_REGEX = /https?:\/\/(?:dx\.)?doi\.org\/[^\s"'<>{}[\]]+/i;
const DOI_INPUT_REGEX = /^(?:doi:\s*)?(10\.\d{4,9}\/[^\s"'<>{}[\]]+)$/i;
const DOI_URL_INPUT_REGEX = /^https?:\/\/(?:dx\.)?doi\.org\//i;
const DOI_TRAILING_REGEX = /[.,;:\]}]+$/;
const PMID_PREFIX_REGEX = /\bPMID:\s*(\d{1,8})\b/i;
const PUBMED_URL_REGEX = /pubmed\.ncbi\.nlm\.nih\.gov\/(\d{1,8})/i;
const PMID_INPUT_REGEX = /^(?:PMID:\s*)?(\d{1,8})$/i;
const PUBMED_INPUT_REGEX =
  /^https?:\/\/pubmed\.ncbi\.nlm\.nih\.gov\/(\d{1,8})\/?$/i;
const ISBN_PREFIX_REGEX =
  /\bISBN(?:-1[03])?:?\s*((?:97[89][-\s]?)?[\dX][\dX\s-]{8,16}[\dX])\b/i;
const ISBN_INPUT_REGEX =
  /^(?:ISBN(?:-1[03])?:?\s*)?((?:97[89][-\s]?)?[\dX][\dX\s-]{8,16}[\dX])$/i;
const ARXIV_PREFIX_REGEX = /\barXiv:\s*(\d{4}\.\d{4,5}(?:v\d+)?)\b/i;
const ARXIV_URL_REGEX = /arxiv\.org\/(?:abs|pdf)\/(\d{4}\.\d{4,5}(?:v\d+)?)/i;
const ARXIV_INPUT_REGEX = /^(?:arXiv:\s*)?(\d{4}\.\d{4,5}(?:v\d+)?)$/i;
const ARXIV_URL_INPUT_REGEX =
  /^https?:\/\/arxiv\.org\/(?:abs|pdf)\/(\d{4}\.\d{4,5}(?:v\d+)?)(?:\.pdf)?$/i;
const NON_ISBN_REGEX = /[^0-9X]/gi;

function trimDoiPunctuation(value: string): string {
  let normalized = value.replace(DOI_TRAILING_REGEX, "");
  while (
    normalized.endsWith(")") &&
    normalized.split(")").length > normalized.split("(").length
  ) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

function doiFromUrl(value: string): string | null {
  try {
    const url = new URL(value);
    const doi = decodeURIComponent(url.pathname.slice(1));
    return DOI_REGEX.test(doi) ? trimDoiPunctuation(doi) : null;
  } catch {
    return null;
  }
}

function doiFrom(text: string): string | null {
  const urlMatch = DOI_URL_REGEX.exec(text)?.[0];
  if (urlMatch) {
    const doi = doiFromUrl(urlMatch);
    if (doi) {
      return doi;
    }
  }
  const match = DOI_REGEX.exec(text);
  return match ? trimDoiPunctuation(match[0]) : null;
}

function pmidFrom(text: string): string | null {
  return (
    PMID_PREFIX_REGEX.exec(text)?.[1] ??
    PUBMED_URL_REGEX.exec(text)?.[1] ??
    null
  );
}

function isbnFrom(text: string): string | null {
  const raw = ISBN_PREFIX_REGEX.exec(text)?.[1];
  if (!raw) {
    return null;
  }
  const normalized = raw.replace(NON_ISBN_REGEX, "").toUpperCase();
  return normalized.length === 10 || normalized.length === 13
    ? normalized
    : null;
}

function arxivFrom(text: string): string | null {
  return (
    ARXIV_PREFIX_REGEX.exec(text)?.[1] ??
    ARXIV_URL_REGEX.exec(text)?.[1] ??
    null
  );
}

/** Extract one authoritative lookup key, preferring DOI over other identifiers. */
export function extractReferenceIdentifier(
  text: string
): ReferenceIdentifier | null {
  const doi = doiFrom(text);
  if (doi) {
    return {
      kind: "doi",
      value: doi.toLowerCase(),
      input: doi,
      provenance: "DOI registry lookup",
    };
  }
  const pmid = pmidFrom(text);
  if (pmid) {
    return {
      kind: "pmid",
      value: pmid,
      input: `PMID:${pmid}`,
      provenance: "PubMed",
    };
  }
  const isbn = isbnFrom(text);
  if (isbn) {
    return {
      kind: "isbn",
      value: isbn,
      input: isbn,
      provenance: "Open Library",
    };
  }
  const arxiv = arxivFrom(text);
  if (arxiv) {
    return {
      kind: "arxiv",
      value: arxiv,
      input: `arXiv:${arxiv}`,
      provenance: "DataCite lookup (arXiv DOI)",
    };
  }
  return null;
}

/**
 * Parse a standalone identifier for the public lookup API.
 *
 * Unlike `extractReferenceIdentifier`, this is deliberately anchored: formatted
 * reference prose is rejected so callers cannot accidentally upload bibliography text
 * to an endpoint whose privacy contract accepts identifiers only.
 */
export function parseReferenceIdentifierInput(
  input: string
): ReferenceIdentifier | null {
  const trimmed = input.trim();
  const doi = DOI_URL_INPUT_REGEX.test(trimmed)
    ? doiFromUrl(trimmed)
    : trimDoiPunctuation(DOI_INPUT_REGEX.exec(trimmed)?.[1] ?? "");
  if (doi) {
    return {
      kind: "doi",
      value: doi.toLowerCase(),
      input: doi,
      provenance: "DOI registry lookup",
    };
  }
  const pmid =
    PMID_INPUT_REGEX.exec(trimmed)?.[1] ??
    PUBMED_INPUT_REGEX.exec(trimmed)?.[1];
  if (pmid) {
    return {
      kind: "pmid",
      value: pmid,
      input: `PMID:${pmid}`,
      provenance: "PubMed",
    };
  }
  const isbnRaw = ISBN_INPUT_REGEX.exec(trimmed)?.[1];
  if (isbnRaw) {
    const isbn = isbnRaw.replace(NON_ISBN_REGEX, "").toUpperCase();
    if (isbn.length === 10 || isbn.length === 13) {
      return {
        kind: "isbn",
        value: isbn,
        input: isbn,
        provenance: "Open Library",
      };
    }
  }
  const arxiv =
    ARXIV_INPUT_REGEX.exec(trimmed)?.[1] ??
    ARXIV_URL_INPUT_REGEX.exec(trimmed)?.[1];
  if (arxiv) {
    return {
      kind: "arxiv",
      value: arxiv,
      input: `arXiv:${arxiv}`,
      provenance: "DataCite lookup (arXiv DOI)",
    };
  }
  return null;
}
